// src/atendimento/Atender/index.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "../../services/api";
import Menu from '../../comum/Menu/menu.jsx';
import Footer from '../../comum/Footer';
import Swal from 'sweetalert2';
import { 
  FaBullhorn, 
  FaUserClock, 
  FaSync, 
  FaWhatsapp, 
  FaInfoCircle, 
  FaBuilding, 
  FaTicketAlt,
  FaCalendarAlt,
  FaFilter
} from 'react-icons/fa';
import './index.css';

// Lista fixa de setores cadastrados
const SETORES_FIXOS = [
  'COMERCIAL',
  'CONTABILIDADE',
  'FINANCEIRO',
  'JURIDICO',
  'PSCICOLOGA',
  'RECURSOS HUMANOS',
  'TI'
];

function FilaAtendimento() {
    const [listaFila, setListaFila] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lojaAtual] = useState(1);

    // Estado do filtro de setor gravado no localStorage
    const [setorFiltro, setSetorFiltro] = useState(() => {
        return localStorage.getItem('filtro_setor_atendimento') || 'TODOS';
    });

    // Estado do checkbox de "somente aguardando" gravado no localStorage
    const [somenteAguardando, setSomenteAguardando] = useState(() => {
        return localStorage.getItem('filtro_somente_aguardando') === 'true';
    });

    const carregarFila = useCallback(() => {
        setLoading(true);
        api.get(`/atendimento/fila?cli_loja=${lojaAtual}`)
            .then(retorno => {
                setListaFila(Array.isArray(retorno.data) ? retorno.data : []);
            })
            .catch(err => {
                console.error("Erro ao carregar fila:", err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [lojaAtual]);

    // Grava o setor no localStorage
    const handleSetorChange = (e) => {
        const novoSetor = e.target.value;
        setSetorFiltro(novoSetor);
        localStorage.setItem('filtro_setor_atendimento', novoSetor);
    };

    // Grava o checkbox de status no localStorage
    const handleAguardandoChange = (e) => {
        const checado = e.target.checked;
        setSomenteAguardando(checado);
        localStorage.setItem('filtro_somente_aguardando', String(checado));
    };

    // Filtra os itens da fila por setor e pelo status "I" (Aguardando)
    const filaFiltrada = useMemo(() => {
        return listaFila.filter(item => {
            const setorMatch = setorFiltro === 'TODOS' || (item.cli_setor || 'Recepção').toUpperCase() === setorFiltro.toUpperCase();
            const statusMatch = !somenteAguardando || item.cli_status === 'I';
            return setorMatch && statusMatch;
        });
    }, [listaFila, setorFiltro, somenteAguardando]);

    const handleChamar = async (visita) => {
        const mensagemSalva = localStorage.getItem('ultima_mensagem_chamada') || "";

        const { value: formValues } = await Swal.fire({
            title: `<span style="color: #8b0000; font-weight: bold;">Chamar ${visita.cli_nome}</span>`,
            icon: 'info',
            html: `
            <div style="text-align: left; font-family: sans-serif;">
                <label for="swal-msg" style="font-weight: bold; font-size: 13px; color: #475569; display: block; margin-bottom: 6px;">
                    Mensagem Adicional / Local de Comparecimento:
                </label>
                <textarea id="swal-msg" class="form-control" 
                    style="width: 100%; height: 90px; resize: none; border-radius: 8px; border: 1px solid #cbd5e1; padding: 10px; font-size: 0.9rem;"
                    placeholder="Ex: Comparecer à recepção ou sala de reuniões">${mensagemSalva}</textarea>
                <small style="display: block; margin-top: 8px; color: #64748b; font-size: 0.78rem;">
                    * Esta mensagem ficará gravada para a próxima chamada.
                </small>
            </div>`,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Disparar Chamada',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#16a34a',
            cancelButtonColor: '#64748b',
            customClass: { popup: 'rounded-4' },
            preConfirm: () => {
                const el = document.getElementById('swal-msg');
                const mensagem = el ? el.value : '';
                localStorage.setItem('ultima_mensagem_chamada', mensagem);
                return { mensagem };
            }
        });

        if (formValues) {
            Swal.fire({
                title: `<span style="color: #8b0000; font-weight: bold; font-size: 1.25rem;">Comunicando...</span>`,
                html: `
                    <div style="padding: 15px 0; display: flex; flex-direction: column; align-items: center; justify-content: center;">
                        <div class="spinner-border" role="status" style="width: 3rem; height: 3rem; color: #8b0000;">
                            <span class="visually-hidden">Enviando...</span>
                        </div>
                        <div style="margin-top: 15px; font-size: 0.95rem; font-weight: 600; color: #475569;">
                            Enviando comando para o servidor, aguarde.
                        </div>
                    </div>
                `,
                allowOutsideClick: false,
                allowEscapeKey: false,
                allowEnterKey: false,
                showConfirmButton: false,
                customClass: { 
                    popup: 'rounded-4 border-0 shadow' 
                }
            });

            const dadosChamada = {
                cli_loja: visita.cli_loja,
                cli_local: visita.cli_local,
                cli_senha: visita.cli_senha,
                cli_datahora: visita.cli_datahora,
                cli_nome: visita.cli_nome,
                cli_empresa: visita.cli_empresa,
                cli_tipo: visita.cli_tipo,
                texto_especifico: formValues.mensagem
            };

            try {
                await api.put('/atendimento/fila', dadosChamada);
                
                Swal.fire({
                    title: 'Chamado!',
                    text: 'Comando enviado com sucesso.',
                    icon: 'success',
                    timer: 1500,
                    showConfirmButton: false,
                    confirmButtonColor: '#8b0000',
                    customClass: { popup: 'rounded-4' }
                });

                carregarFila();
            } catch (error) {
                Swal.fire({
                    icon: 'error',
                    title: 'Erro',
                    text: 'Falha ao processar chamada no banco local.',
                    confirmButtonColor: '#8b0000',
                    customClass: { popup: 'rounded-4' }
                });
            }
        }
    };

    useEffect(() => {
        carregarFila();
        const interval = setInterval(carregarFila, 30000);
        return () => clearInterval(interval);
    }, [carregarFila]);

    const decodificarDescricao = (strBase64) => {
        if (!strBase64) return "";
        try {
            return decodeURIComponent(window.atob(strBase64));
        } catch (e) {
            return "Descrição indisponível";
        }
    };

    return (
        <div className="fila-page-wrapper">
            <Menu modulo="ARBOR" />

            <div className="container-fluid px-3 px-md-4 mt-4 max-width-container pb-5">
                
                {/* CABEÇALHO */}
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 pb-2 border-bottom gap-2">
                    <div>
                        <h5 className="fw-bold m-0 text-dark d-flex align-items-center gap-2">
                            <FaUserClock className="text-custom-red" />
                            Atendimento Local - Fila da Recepção
                        </h5>
                        <span className="small text-muted">
                            Acompanhamento em tempo real de visitantes e clientes na loja.
                        </span>
                    </div>

                    <button 
                        type="button" 
                        className="btn btn-outline-secondary btn-sm fw-semibold rounded-3 d-flex align-items-center gap-2"
                        onClick={carregarFila}
                        disabled={loading}
                    >
                        <FaSync className={loading ? "spin" : ""} /> Atualizar
                    </button>
                </div>

                {/* BARRA DE FILTRO POR SETOR E STATUS (PERSISTENTES NO LOCALSTORAGE) */}
                <div className="card border-0 shadow-sm rounded-3 p-3 mb-4 bg-white">
                    <div className="row align-items-center g-3">
                        <div className="col-auto d-flex align-items-center gap-2 text-dark fw-bold small">
                            <FaFilter style={{ color: '#8b0000' }} />
                            <span>Filtrar por Setor:</span>
                        </div>
                        <div className="col-md-4 col-sm-6">
                            <select 
                                className="form-select form-select-sm fw-semibold rounded-2"
                                value={setorFiltro}
                                onChange={handleSetorChange}
                                style={{ borderColor: '#cbd5e1' }}
                            >
                                <option value="TODOS">-- Todos os Setores --</option>
                                {SETORES_FIXOS.map((setor, index) => (
                                    <option key={index} value={setor}>
                                        {setor}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* CHECKBOX PARA EXIBIR APENAS STATUS "I" (AGUARDANDO) */}
                        <div className="col-auto ms-sm-2">
                            <div className="form-check form-switch m-0 d-flex align-items-center gap-2">
                                <input 
                                    className="form-check-input mt-0" 
                                    type="checkbox" 
                                    id="checkSomenteAguardando"
                                    checked={somenteAguardando}
                                    onChange={handleAguardandoChange}
                                    style={{ cursor: 'pointer', accentColor: '#8b0000' }}
                                />
                                <label className="form-check-label small fw-bold text-dark cursor-pointer mb-0" htmlFor="checkSomenteAguardando" style={{ userSelect: 'none' }}>
                                    Exibir somente Aguardando
                                </label>
                            </div>
                        </div>

                        {(setorFiltro !== 'TODOS' || somenteAguardando) && (
                            <div className="col-auto">
                                <span className="badge bg-light text-dark border px-2 py-1 small">
                                    Registos: <strong>{filaFiltrada.length}</strong>
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* TABELA DESKTOP */}
                <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-4 d-none d-md-block bg-white">
                    <div className="table-responsive">
                        <table className="table table-hover align-middle m-0 fila-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '150px' }}>Data / Hora</th>
                                    <th className="text-center" style={{ width: '90px' }}>Senha</th>
                                    <th>Visitante / Empresa</th>
                                    <th className="text-center" style={{ width: '140px' }}>Setor</th>
                                    <th className="text-center" style={{ width: '120px' }}>Status</th>
                                    <th className="text-center" style={{ width: '120px' }}>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && filaFiltrada.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-5">
                                            <div className="spinner-border spinner-border-sm me-2" role="status" style={{ color: '#8b0000' }}></div>
                                            <span className="text-muted small fw-semibold">Buscando fila de atendimento...</span>
                                        </td>
                                    </tr>
                                ) : filaFiltrada.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-5 text-muted small">
                                            Nenhum visitante encontrado com os filtros selecionados.
                                        </td>
                                    </tr>
                                ) : (
                                    filaFiltrada.map((visita) => (
                                        <tr key={`${visita.cli_loja}-${visita.cli_senha}-${visita.cli_datahora}`}>
                                            <td className="small text-muted fw-semibold">
                                                <FaCalendarAlt className="me-1 text-secondary" />
                                                {visita.cli_datahora}
                                            </td>
                                            <td className="text-center">
                                                <span className="badge bg-dark rounded-2 px-2 py-1 fs-6">
                                                    #{visita.cli_senha}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <strong className="text-dark fs-6">{visita.cli_nome}</strong>
                                                    {visita.cli_whatsapp && (
                                                        <a 
                                                            href={`https://api.whatsapp.com/send?phone=55${visita.cli_whatsapp.replace(/\D/g, '')}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-success text-decoration-none d-inline-flex align-items-center"
                                                            title="Chamar no WhatsApp"
                                                        >
                                                            <FaWhatsapp className="fs-5" />
                                                        </a>
                                                    )}
                                                </div>
                                                
                                                {visita.cli_empresa && (
                                                    <small className="text-muted d-block mt-1">
                                                        <FaBuilding className="me-1" /> {visita.cli_empresa}
                                                    </small>
                                                )}

                                                {visita.cli_descricao && (
                                                    <div className="fila-obs-box mt-1">
                                                        <FaInfoCircle className="me-1 text-danger" />
                                                        {decodificarDescricao(visita.cli_descricao)}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                <span className="badge bg-light text-dark border fw-semibold px-2 py-1">
                                                    {visita.cli_setor || 'Recepção'}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                {visita.cli_status === 'I' ? (
                                                    <span className="badge bg-warning text-dark px-2 py-1">
                                                        Aguardando
                                                    </span>
                                                ) : visita.cli_status === 'C' || visita.cli_status === 'A' ? (
                                                    <span className="badge bg-info text-dark px-2 py-1">
                                                        Chamado
                                                    </span>
                                                ) : (
                                                    <span className="badge bg-light text-dark px-2 py-1">
                                                        {visita.cli_status}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                <button 
                                                    type="button" 
                                                    className="btn btn-success btn-sm fw-bold px-3 rounded-3 d-inline-flex align-items-center justify-content-center gap-1" 
                                                    onClick={() => handleChamar(visita)}
                                                >
                                                    <FaBullhorn /> Chamar
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* CARDS MOBILE */}
                <div className="d-md-none mb-4">
                    {loading && filaFiltrada.length === 0 ? (
                        <div className="text-center py-5">
                            <div className="spinner-border spinner-border-sm me-2" role="status" style={{ color: '#8b0000' }}></div>
                            <span className="text-muted small fw-semibold">Carregando fila...</span>
                        </div>
                    ) : filaFiltrada.length === 0 ? (
                        <div className="text-center py-5 text-muted small bg-white rounded-3 border">
                            Nenhum visitante encontrado com os filtros selecionados.
                        </div>
                    ) : (
                        filaFiltrada.map((visita) => (
                            <div key={`${visita.cli_loja}-${visita.cli_senha}-${visita.cli_datahora}`} className="card border-0 shadow-sm rounded-3 mb-3 p-3 bg-white">
                                <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-2">
                                    <span className="badge bg-dark rounded-2 px-2 py-1 fs-6 d-flex align-items-center gap-1">
                                        <FaTicketAlt /> #{visita.cli_senha}
                                    </span>
                                    <span className="small text-muted fw-semibold">
                                        {visita.cli_datahora}
                                    </span>
                                </div>

                                <div className="mb-2">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <strong className="text-dark fs-6">{visita.cli_nome}</strong>
                                        {visita.cli_whatsapp && (
                                            <a 
                                                href={`https://api.whatsapp.com/send?phone=55${visita.cli_whatsapp.replace(/\D/g, '')}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-success fs-5 text-decoration-none"
                                            >
                                                <FaWhatsapp />
                                            </a>
                                        )}
                                    </div>
                                    {visita.cli_empresa && (
                                        <small className="text-muted d-block mt-1">
                                            <FaBuilding className="me-1" /> {visita.cli_empresa}
                                        </small>
                                    )}
                                </div>

                                {visita.cli_descricao && (
                                    <div className="fila-obs-box mb-3">
                                        <FaInfoCircle className="me-1 text-danger" />
                                        {decodificarDescricao(visita.cli_descricao)}
                                    </div>
                                )}

                                <div className="d-flex justify-content-between align-items-center border-top pt-2">
                                    <div>
                                        <span className="badge bg-light text-dark border me-1">
                                            {visita.cli_setor || 'Recepção'}
                                        </span>
                                        {visita.cli_status === 'I' ? (
                                            <span className="badge bg-warning text-dark">Aguardando</span>
                                        ) : (
                                            <span className="badge bg-info text-dark">Chamado</span>
                                        )}
                                    </div>

                                    <button 
                                        type="button" 
                                        className="btn btn-success btn-sm fw-bold px-3 rounded-3 d-inline-flex align-items-center gap-1" 
                                        onClick={() => handleChamar(visita)}
                                    >
                                        <FaBullhorn /> Chamar
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>

            </div>

            <Footer />
        </div>
    );
}

export default FilaAtendimento;